import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { DSFeature } from '@dataswift/shared';
import { useDispatch } from 'react-redux';
import { Typography, useMediaQuery, useTheme } from '@material-ui/core';

import DSLogo from '../../assets/DataswiftLogo';
import DSLogoSmall from '../../assets/DataswiftLogoSmall';
import AuthService from '../../services/AuthService';
import { resetApps } from '../../redux/applications/actions/AppActions';
import { resetOrganisation } from '../../redux/organisation/actions/OrganisationActions';
import { RouterPaths } from '../../constants/routes';
import SignOutIcon from '../../assets/icons/sign_out';
import useStyles from './styles';
import { DSTheme } from '../../styles/themes/types';
import ThemeToggle from './ThemeToggle/ThemeToggle';
import { AvailableRoles, MenuItem, menuItems, subMenuItems } from '../../constants/navigation';
import { resetUserAction } from '../../redux/user/actions/UserActions';
import { resetAdminDevelopers } from '../../redux/admin/actions/AdminUserActions';
import { resetAdminApps } from '../../redux/admin/actions/AdminAppActions';

type RenderItemType = {
  selectedItem: string;
  onItemClick: (item: MenuItem) => void;
  classes: Record<string, string>;
  theme: DSTheme;
};

const renderMenuItem = (
  item: MenuItem,
  { selectedItem, onItemClick, classes, theme }: RenderItemType,
) => {
  const selected = selectedItem === item.displayName;
  const { Icon } = item;
  return (
    <div
      key={item.displayName}
      className={`text-standard ${
        selected ? classes.sideNavMenuItemSelected : classes.sideNavMenuItem
      }`}
      onClick={() => onItemClick(item)}
    >
      <div className={classes.sideMenuIcon}>
        <Icon
          stroke={selected ? theme.palette.primary.contrastText : theme.palette.text.navigation}
        />
      </div>
      <div className={selected ? classes.sideNavMenuItemTextSelected : classes.sideNavMenuItemText}>
        {item.displayName}
      </div>
    </div>
  );
};

type SideNavProps = {
  userRole?: AvailableRoles;
};

const SideNav: React.FC<SideNavProps> = ({ userRole = 'Developer' }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme<DSTheme>();
  const classes = useStyles();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  const logo = isWideScreen ? (
    <DSLogo fill={theme.palette.text.logo} width="160px" height="27px" />
  ) : (
    <DSLogoSmall fill={theme.palette.text.logo} width="41" height="40" />
  );

  const selectedItem =
    [...menuItems[userRole], ...subMenuItems[userRole]].find(
      (item) => location.pathname === item.link,
    )?.displayName || '';

  const logout = () => {
    if (AuthService.loggedIn()) {
      dispatch(resetApps());
      dispatch(resetOrganisation());
      dispatch(resetUserAction());
      dispatch(resetAdminDevelopers());
      dispatch(resetAdminApps());
      AuthService.logout();
    }

    history.replace(RouterPaths.home);
  };

  const onItemClick = (item: MenuItem) => history.push(item.link);

  const itemProps = {
    selectedItem,
    onItemClick,
    classes,
    theme,
  };

  return (
    <>
      <div className={classes.sideNav}>
        <div className={classes.logo}>
          {logo}

          {userRole !== 'Developer' && (
            <Typography className={classes.adminPortalText}>Admin Portal</Typography>
          )}

          <DSFeature name="beta">
            <Typography variant="body2" className={classes.betaText}>
              beta
            </Typography>
          </DSFeature>
        </div>

        <div className={classes.sideNavMenu}>
          <div>{menuItems[userRole].map((item) => renderMenuItem(item, itemProps))}</div>

          <div>
            <ThemeToggle />
            {subMenuItems[userRole].map((item) => renderMenuItem(item, itemProps))}

            <div className={classes.sideNavMenuItem} onClick={logout}>
              <div className={classes.sideMenuIcon}>
                <SignOutIcon stroke={theme.palette.text.navigation} />
              </div>

              <div className={classes.sideNavMenuItemText}>Sign Out</div>
            </div>
          </div>
        </div>
      </div>
      <div className={classes.sideNavSpacer} />
    </>
  );
};

export default SideNav;
